import { useState, useEffect, useRef, useCallback } from 'react';
import useAxios from './useAxios';
import Constants from '../Constants';
import { debounce } from '../utils/helper';
import { toaster } from '../utils/toast';

const useLifeCarePlanReassignment = ({ caseId, currentOwner, onReassigned }) => {
    const [users, setUsers] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const { getData, isLoading: isSearching } = useAxios();
    const { putData, isLoading: isReassigning } = useAxios();

    const debouncedSearch = useRef(debounce((val) => setSearchText(val), 500)).current;

    useEffect(() => {
        if (!currentOwner?._id) return;
        setSelectedUser(currentOwner);
    }, [currentOwner]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            const url = `${Constants.ApiUrl.user.assignableUsers}?searchText=${searchText}`;
            const response = await getData(url);
            if (!response?.success) return;

            const currentOwnerId = currentOwner?._id || currentOwner;
            const filtered = (response?.data?.users ?? []).filter(
                (u) => u?._id !== currentOwnerId
            );
            setUsers(filtered);
        };
        fetchUsers();
    }, [getData, searchText, currentOwner]);

    const handleInputChange = useCallback((e) => {
        const val = e.target.value;
        if (selectedUser) {
            setSelectedUser(null);
            setSearchText('');
        }
        setInputValue(val);
        debouncedSearch(val);
        setIsDropdownOpen(true);
    }, [debouncedSearch, selectedUser]);

    const openDropdown = useCallback(() => {
        setIsDropdownOpen(true);
    }, []);

    const handleSelectUser = useCallback((user) => {
        setSelectedUser(user);
        setIsDropdownOpen(false);
        setInputValue('');
    }, []);

    const currentOwnerId = currentOwner?._id || currentOwner;
    const isEnabled = !!selectedUser && selectedUser?._id !== currentOwnerId;

    const currentOwnerName = currentOwner?._id
        ? [currentOwner?.firstName, currentOwner?.lastName].filter(Boolean).join(' ')
        : '';

    const assignmentLabel = currentOwnerName
        ? `Currently assigned to ${currentOwnerName}`
        : null;

    const selectedUserName = selectedUser
        ? [selectedUser?.firstName, selectedUser?.lastName].filter(Boolean).join(' ')
        : '';

    const requestReassign = useCallback(() => {
        if (!selectedUser) return;
        setIsConfirmModalOpen(true);
    }, [selectedUser]);

    const cancelReassign = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    const confirmReassign = useCallback(async () => {
        setIsConfirmModalOpen(false);
        const response = await putData(Constants.ApiUrl.certifiedLifecarePlan.reassign, { caseId, newUserId: selectedUser?._id });

        if (!response?.success) {
            toaster({ message: response?.message || 'Failed to reassign case', success: false });
            return;
        }

        toaster({ message: 'Case reassigned successfully', success: true });
        onReassigned?.(response?.data);
        setSelectedUser(null);
    }, [caseId, selectedUser, putData, onReassigned]);

    return {
        users,
        selectedUser,
        selectedUserName,
        assignmentLabel,
        isEnabled,
        isSearching,
        isReassigning,
        isConfirmModalOpen,
        inputValue,
        isDropdownOpen,
        dropdownRef,
        handleInputChange,
        openDropdown,
        handleSelectUser,
        requestReassign,
        confirmReassign,
        cancelReassign,
    };
};

export default useLifeCarePlanReassignment;
